interface PushSubscriptionData {
  endpoint: string;
  p256dh: string;
  auth: string;
}

class NotificationService {
  private static instance: NotificationService;
  private isInitialized = false;

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  async initialize(): Promise<boolean> {
    if (this.isInitialized) return true;

    try {
      // Check if notifications and service workers are supported
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        console.warn('Notifications or Service Workers not supported');
        return false;
      }

      // Register service worker
      await this.registerServiceWorker();
      
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize notification service:', error);
      return false;
    }
  }

  async requestPermissionAndSubscribe(): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        const initialized = await this.initialize();
        if (!initialized) return false;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return false;
      }

      // Subscribe to push notifications
      await this.subscribeToPush();
      return true;
    } catch (error) {
      console.error('Failed to request permission and subscribe:', error);
      return false;
    }
  }

  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    const registration = await navigator.serviceWorker.register('/static/sw.js');
    console.log('Service Worker registered:', registration);
    return registration;
  }

  private async subscribeToPush(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        throw new Error('No service worker registration found');
      }

      // You'll need to set this in your environment or get it from your backend
      const VAPID_PUBLIC_KEY = await this.getVapidPublicKey();
      
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription);
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      throw error;
    }
  }

  private async getVapidPublicKey(): Promise<string> {
    try {
      // Fetch VAPID public key from backend
      const response = await fetch('/api/push/vapid-public-key');
      if (!response.ok) {
        throw new Error(`Failed to get VAPID public key: ${response.status}`);
      }
      const data = await response.json();
      return data.publicKey;
    } catch (error) {
      console.error('Failed to fetch VAPID public key:', error);
      // Fallback - you'll need to replace this with your actual VAPID public key
      throw new Error('Unable to obtain VAPID public key from server');
    }
  }

  private async sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    const subscriptionData = this.extractSubscriptionData(subscription);
    
    // Get device fingerprint for linking to device management
    const { deviceFingerprinting } = await import('../services/deviceFingerprinting');
    const deviceInfo = deviceFingerprinting.getDeviceInfo();
    
    const { storage } = await import('../services/storage');
    const token = await storage.getItem('jwt_token');
    
    const response = await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        ...subscriptionData,
        device_fingerprint: deviceInfo.fingerprint  // Link to device management
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to send subscription to backend: ${response.status}`);
    }
    
    console.log('📱🔔 Push subscription linked to device:', deviceInfo.fingerprint);
  }

  private extractSubscriptionData(subscription: PushSubscription): PushSubscriptionData {
    const subscriptionJson = subscription.toJSON();
    return {
      endpoint: subscriptionJson.endpoint!,
      p256dh: subscriptionJson.keys!.p256dh!,
      auth: subscriptionJson.keys!.auth!
    };
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  getPermissionStatus(): NotificationPermission {
    return 'Notification' in window ? Notification.permission : 'denied';
  }

  isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  async setNotificationHour(hour: number): Promise<boolean> {
    try {
      const { storage } = await import('../services/storage');
      const token = await storage.getItem('jwt_token');
      const response = await fetch('/api/push/set_hour', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ hour })
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to set notification hour:', error);
      return false;
    }
  }
}

export default NotificationService; 